import React from 'react';

/**
 * Shared UI primitives used by every CRM tab to give a consistent look:
 *   - <CRMTabHero>          gradient hero card at the top of a tab.
 *   - <CRMSectionHeader>    drop-in replacement for the bare <h2 class="crm-section-title">
 *                           used at the top of each .crm-form-section card. Adds a coloured
 *                           icon tile, count chip, optional action links, and helper line
 *                           without touching the section's body content.
 *
 * Both rely on CSS in CRMProfileScreen.css under the `.crm-tab-modern` parent
 * scope. Functionality of the consuming tabs is untouched — these are pure
 * presentation components.
 */

// ---- Inline icon set (no external dep, no emoji) -------------------------
// Each icon is a small <svg> sized to the section glyph slot. Caller passes
// e.g. <CRMSectionHeader icon={<TabIcons.Wallet />} ... />.
const baseIconProps = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
};

export const TabIcons = {
    // Generic
    Services: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
    ),
    Link: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
    ),
    Search: (props) => (
        <svg {...baseIconProps} {...props}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    ),
    // Partner Profile
    Profile: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    ),
    Building: (props) => (
        <svg {...baseIconProps} {...props}>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" />
        </svg>
    ),
    MapPin: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
        </svg>
    ),
    // Banking
    Wallet: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
            <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
            <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
        </svg>
    ),
    CreditCard: (props) => (
        <svg {...baseIconProps} {...props}>
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
    ),
    Calendar: (props) => (
        <svg {...baseIconProps} {...props}>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
    ),
    Receipt: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2" />
            <line x1="8" y1="8" x2="16" y2="8" />
            <line x1="8" y1="12" x2="16" y2="12" />
            <line x1="8" y1="16" x2="12" y2="16" />
        </svg>
    ),
    // KYC / Compliance
    Shield: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
    ),
    ShieldCheck: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
        </svg>
    ),
    AlertTriangle: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    UserCheck: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <polyline points="17 11 19 13 23 9" />
        </svg>
    ),
    // AI / Documents / History
    FileText: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <line x1="10" y1="9" x2="8" y2="9" />
        </svg>
    ),
    History: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
            <polyline points="3 3 3 8 8 8" />
            <polyline points="12 7 12 12 15 14" />
        </svg>
    ),
    Sparkle: (props) => (
        <svg {...baseIconProps} {...props}>
            <path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4z" />
            <path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" />
        </svg>
    ),
    Globe: (props) => (
        <svg {...baseIconProps} {...props}>
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
    ),
    TrendingUp: (props) => (
        <svg {...baseIconProps} {...props}>
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
            <polyline points="17 6 23 6 23 12" />
        </svg>
    ),
    Activity: (props) => (
        <svg {...baseIconProps} {...props}>
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
    ),
    Database: (props) => (
        <svg {...baseIconProps} {...props}>
            <ellipse cx="12" cy="5" rx="9" ry="3" />
            <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
            <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        </svg>
    ),
};

/**
 * CRMTabHero
 *
 * Hero card placed at the top of every CRM tab. Provides title + 1-line
 * description with a coloured icon tile and a subtle gradient wash.
 *
 * Props:
 *   - icon:        ReactNode (typically an icon from TabIcons)
 *   - title:       string
 *   - description: string
 *   - tone:        optional "blue" | "indigo" | "amber" | "emerald" | "violet"
 *                  (defaults to "blue" — primary CRM accent)
 */
export const CRMTabHero = ({ icon, title, description, tone = 'blue' }) => {
    return (
        <div className="crm-tab-hero" data-tone={tone}>
            <span className="crm-tab-hero-icon" aria-hidden="true">
                {icon}
            </span>
            <div className="crm-tab-hero-text">
                <h1 className="crm-tab-hero-title">{title}</h1>
                {description && (
                    <p className="crm-tab-hero-desc">{description}</p>
                )}
            </div>
        </div>
    );
};

/**
 * CRMSectionHeader
 *
 * Drop-in replacement for `<h2 className="crm-section-title">Title</h2>`
 * inside an existing `<div className="crm-form-section">`. Renders an icon
 * tile, the title, an optional count chip, optional inline action links, and
 * an optional one-line helper paragraph below.
 *
 * Props:
 *   - icon:       ReactNode (typically an icon from TabIcons). Optional.
 *   - iconTone:   "blue" | "indigo" | "amber" | "emerald" | "violet" (default "blue")
 *   - title:      string  (required)
 *   - count:      number | null  (optional — shows count chip when provided)
 *   - countLabel: string (default "selected"; e.g. "linked", "documents")
 *   - actions:    ReactNode  (optional — rendered to the right; usually link buttons)
 *   - help:       ReactNode  (optional — one-line helper under the header)
 *   - children:   ReactNode  (optional — extra header chrome like a status pill)
 */
export const CRMSectionHeader = ({
    icon,
    iconTone = 'blue',
    title,
    count = null,
    countLabel = 'selected',
    actions = null,
    help = null,
    children = null,
}) => {
    const showCount = count !== null && count !== undefined;
    return (
        <>
            <header className="crm-tab-section-head">
                <div className="crm-tab-section-head-titleblock">
                    {icon && (
                        <span className="crm-tab-section-head-icon" data-tone={iconTone}>
                            {icon}
                        </span>
                    )}
                    <h2 className="crm-tab-section-head-title">{title}</h2>
                    {showCount && (
                        <span className="crm-tab-section-head-count">
                            {count} {countLabel}
                        </span>
                    )}
                    {children}
                </div>
                {actions && (
                    <div className="crm-tab-section-head-actions">
                        {actions}
                    </div>
                )}
            </header>
            {help && (
                <p className="crm-tab-section-head-help">{help}</p>
            )}
        </>
    );
};

/**
 * CRMHeaderLinkButton
 *
 * Small subtle text button used inside CRMSectionHeader's `actions` slot,
 * e.g. "Select all" / "Clear" / "View history".
 */
export const CRMHeaderLinkButton = ({ onClick, disabled = false, children, ...rest }) => (
    <button
        type="button"
        className="crm-tab-section-head-linkbtn"
        onClick={onClick}
        disabled={disabled}
        {...rest}
    >
        {children}
    </button>
);

export const CRMHeaderDivider = () => (
    <span className="crm-tab-section-head-divider" aria-hidden="true">·</span>
);

export default {
    CRMTabHero,
    CRMSectionHeader,
    CRMHeaderLinkButton,
    CRMHeaderDivider,
    TabIcons,
};
